/**
 * Map Equipment Modal
 *
 * Modal dialog for linking a cloud host to Warehouse equipment.
 * Shows list of unmapped servers and allows selection.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { Server, Search, Loader2, AlertCircle } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getItems } from '../../services'
import { createMapping } from '../../services/equipment-mapping'
import type { Item } from '../../types/response'
import type { CreateMappingRequest } from '../../types/mapping'

interface MapEquipmentModalProps {
    open: boolean
    onClose: () => void
    host: {
        id: string
        name: string
        zoneId: string
        zoneName: string
        providerType: string // Category: compute, cdn, storage
        providerCode: string // Animal code: panda, bear
        providerName?: string
    }
}

const MapEquipmentModal = ({ open, onClose, host }: MapEquipmentModalProps) => {
    const queryClient = useQueryClient()
    const [search, setSearch] = useState('')
    const [selectedEquipment, setSelectedEquipment] = useState<Item | null>(
        null
    )

    // Fetch unmapped equipment (servers only) - reuses getItems like ItemSelector
    const {
        data: itemsData,
        isLoading,
        error,
    } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.WAREHOUSE_ITEMS,
            'equipment',
            'server',
            search,
            'unmapped',
        ],
        queryFn: () =>
            getItems({
                item_type: 'equipment',
                category: 'server',
                unmapped: true,
                search,
                page_size: 50,
            }),
        enabled: open,
    })

    const items = itemsData?.results || []

    // Status badge variant - same as ItemSelector
    const getStatusBadgeVariant = (status: string) => {
        switch (status.toLowerCase()) {
            case 'available':
            case 'in_warehouse':
                return 'default'
            case 'in_use':
            case 'in_datacenter':
                return 'secondary'
            case 'damaged':
                return 'destructive'
            default:
                return 'outline'
        }
    }

    // Create mapping mutation
    const createMappingMutation = useMutationWithHandler(
        {
            mutationFn: (data: CreateMappingRequest) => createMapping(data),
            onSuccess: () => {
                toast.success(
                    `${selectedEquipment?.name} linked to ${host.name}`
                )
                // Invalidate related queries
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.CAPACITY_ZONE, host.zoneId],
                })
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS],
                })
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.WAREHOUSE_ZONE_MAPPINGS, host.zoneId],
                })
                handleClose()
            },
        },
        { showErrorToast: true }
    )

    const handleClose = () => {
        setSearch('')
        setSelectedEquipment(null)
        onClose()
    }

    const handleConfirm = () => {
        if (!selectedEquipment) return

        createMappingMutation.mutate({
            item_id: selectedEquipment.id,
            zone_id: host.zoneId,
            zone_name: host.zoneName,
            host_id: host.id,
            provider_type: host.providerType, // Category: compute, cdn, storage
            provider_code: host.providerCode, // Animal code: panda, bear
            provider_name: host.providerName,
        })
    }

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Link Equipment to Host</DialogTitle>
                    <DialogDescription>
                        Select Warehouse equipment to link to{' '}
                        <span className="font-medium">{host.name}</span>
                    </DialogDescription>
                </DialogHeader>

                {/* Search input */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by name, service tag, or serial..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>

                {/* Equipment list */}
                <ScrollArea className="h-[300px] rounded-md border">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : error ? (
                        <Alert variant="destructive" className="m-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                Failed to load equipment list
                            </AlertDescription>
                        </Alert>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                            <Server className="h-8 w-8 mb-2" />
                            <p>No unmapped servers found</p>
                            {search && (
                                <p className="text-sm">
                                    Try a different search term
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="divide-y">
                            {items.map((item) => {
                                const isSelected =
                                    selectedEquipment?.id === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() =>
                                            setSelectedEquipment(item)
                                        }
                                        className={cn(
                                            'w-full flex items-center gap-4 p-4 text-left transition-colors',
                                            'hover:bg-muted/50',
                                            isSelected && 'bg-primary/5'
                                        )}
                                    >
                                        {/* Radio (single-select) */}
                                        <div
                                            className={cn(
                                                'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                                                isSelected
                                                    ? 'bg-primary border-primary'
                                                    : 'border-muted-foreground'
                                            )}
                                        >
                                            {isSelected && (
                                                <div className="w-2 h-2 rounded-full bg-primary-foreground" />
                                            )}
                                        </div>

                                        {/* Item Info - exact same as ItemSelector */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium truncate">
                                                    {item.name}
                                                </span>
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    {item.item_type}
                                                </Badge>
                                            </div>
                                            <div className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 mt-1">
                                                {item.service_tag && (
                                                    <span>
                                                        ST: {item.service_tag}
                                                    </span>
                                                )}
                                                {item.serial_number && (
                                                    <span>
                                                        SN: {item.serial_number}
                                                    </span>
                                                )}
                                                <span>
                                                    Category: {item.category}
                                                </span>
                                                {item.current_location && (
                                                    <span>
                                                        @{' '}
                                                        {
                                                            item
                                                                .current_location
                                                                .name
                                                        }
                                                    </span>
                                                )}
                                                {item.rack_position_u && (
                                                    <span className="text-blue-600">
                                                        U{item.rack_position_u}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <Badge
                                            variant={getStatusBadgeVariant(
                                                item.status
                                            )}
                                        >
                                            {item.status}
                                        </Badge>
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </ScrollArea>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={
                            !selectedEquipment ||
                            createMappingMutation.isPending
                        }
                    >
                        {createMappingMutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Linking...
                            </>
                        ) : (
                            'Link Equipment'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default MapEquipmentModal
